import { useCallback } from "react";
import {
  autocompleteClasses,
  Chip,
  FormControl,
  inputBaseClasses,
  Autocomplete as MuiAutocomplete,
  outlinedInputClasses,
  Popper,
  styled,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import TextFieldCustom from "../TextFieldCustom/TextFieldCustom";
import ListboxComponent from "./ListBoxComponent";

const MuiAutocompleteStyled = styled(MuiAutocomplete)({
  [`& .${autocompleteClasses.endAdornment}`]: {
    padding: 0,
  },
  [`& .${autocompleteClasses.inputRoot}.${inputBaseClasses.root}`]: {
    paddingTop: "5px",
    paddingBottom: "5px",
  },
  [`& .${autocompleteClasses.input}.${inputBaseClasses.input}.${outlinedInputClasses.input}`]:
    {
      paddingTop: 0,
      paddingBottom: 0,
    },
  [`.${autocompleteClasses.listbox} .${autocompleteClasses.option}`]: {
    fontSize: "14px",
  },
});

const MuiPopperStyled = styled(Popper)({
  [`& .${autocompleteClasses.listbox} .${autocompleteClasses.option}`]: {
    fontSize: "14px",
  },
});

const viewModeStyle = {
  [`& .${autocompleteClasses.endAdornment}`]: {
    display: "none",
  },
};

const Autocomplete = ({
  className,
  classNames,
  corners = "round", // 'square' | 'round'
  helperText,
  id,
  isError,
  isMultiple,
  isDisabled,
  isViewMode,
  onChange,
  options,
  placeholder,
  label,
  value,
  filterOptions,
  noOptionsText,
  inputValue,
  onInputChange,
  disablePortal,
  sx,
}) => {
  const { t } = useTranslation();
  const placeholderFinal =
    placeholder || t("Components.Autocomplete.Placeholder");

  const handleChange = (_, newValue) => {
    onChange?.(newValue);
  };

  const handleFilterOptions = useCallback(
    (options, { inputValue }) => {
      if (typeof filterOptions === "function") {
        return filterOptions(options, { inputValue });
      }

      return options?.filter((option) =>
        option.label?.toLowerCase().startsWith(inputValue.toLowerCase())
      );
    },
    [filterOptions]
  );

  const finalSx = isViewMode ? { ...viewModeStyle, ...sx } : sx;

  return (
    <FormControl fullWidth>
      <MuiAutocompleteStyled
        id={id}
        fullWidth
        value={value}
        multiple={isMultiple}
        disabled={isViewMode || isDisabled}
        onChange={handleChange}
        options={options}
        slots={{ popper: MuiPopperStyled }}
        slotProps={{ listbox: { component: ListboxComponent } }}
        filterOptions={handleFilterOptions}
        noOptionsText={noOptionsText}
        disablePortal={disablePortal}
        inputValue={inputValue}
        onInputChange={onInputChange}
        getOptionDisabled={(option) => option.isDisabled}
        getOptionLabel={(option) =>
          option.label ?? options?.find((o) => o.value === option)?.label ?? ""
        }
        renderInput={(params) => {
          const { className, ...restInputProps } = params.InputProps;
          return (
            <TextFieldCustom
              {...params}
              slotProps={{
                input: { ...restInputProps, ref: params.InputProps.ref },
                htmlInput: { ...params.inputProps },
              }}
              label={label}
              isDisabled={isViewMode || isDisabled}
              placeholder={placeholderFinal}
              helperText={helperText}
              isError={isError}
              isViewMode={isViewMode}
              corners={corners}
              classNames={classNames}
            />
          );
        }}
        renderOption={(props, option, state) => [props, option, state.index]}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip key={key} label={option.label} size="small" {...tagProps} />
            );
          })
        }
        className={className}
        sx={finalSx}
      />
    </FormControl>
  );
};

export default Autocomplete;
